import React, { useState, useEffect } from 'react';
import { Typography, Button, Spin, message, Badge } from 'antd';
import { IconBell, IconCheck, IconReceipt2, IconTruckDelivery, IconAlertCircle, IconTicket } from '@tabler/icons-react';
import NotificationServ from '../service/NotificationServ';
import { useNavigate } from 'react-router-dom';
import { fromNow } from '../util/dateUtils';
import EmptyState from '../util/EmptyState';
import { formatNotificationText } from '../util/unitFormatter';

const { Text } = Typography;

const NotificationsPage = ({ role }) => {
    const [loading, setLoading] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const navigate = useNavigate();

    // Handle Super Admin case where App.js passes 'admin'
    const storedRole = localStorage.getItem("role");
    const effectiveRole = (role === 'admin' && storedRole === 'superadmin') ? 'superadmin' : role;


    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const res = await NotificationServ.getNotifications({});
            if (res && res.data) {
                setNotifications(res.data);
            }
        } catch (error) {
            message.error("Failed to load notifications");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications();
    }, [effectiveRole]);

    const handleNotificationClick = async (item) => {
        if (!item.isRead) {
            try {
                await NotificationServ.markAsRead(item._id);
                setNotifications(prev => prev.map(n => n._id === item._id ? { ...n, isRead: true } : n));
                window.dispatchEvent(new Event("notification-refresh"));
            } catch (error) {
                console.error("Failed to mark as read", error);
            }
        }

        // Navigation logic...
        const lowerTitle = item.title?.toLowerCase() || "";
        const roleLower = role?.toLowerCase();
        if (item.orderId || lowerTitle.includes('order')) {
            if (roleLower === 'seller') {
                navigate('/seller/orders', { state: { highlightOrderId: item.orderId } });
            } else if (['admin', 'superadmin'].includes(roleLower)) {
                if (lowerTitle.includes('return') || lowerTitle.includes('cancel')) {
                    navigate('/admin/returns', { state: { highlightOrderId: item.orderId } });
                } else if (lowerTitle.includes('assignment') || lowerTitle.includes('delivery')) {
                    navigate('/admin/delivery-ops', { state: { highlightOrderId: item.orderId } });
                } else {
                    navigate('/admin/orders', { state: { highlightOrderId: item.orderId } });
                }
            } else {
                const isIssue = lowerTitle.includes('order update') || lowerTitle.includes('replace order');
                navigate('/buyer/orders', { 
                    state: { 
                        highlightOrderId: item.orderId,
                        ...(isIssue && { openIssueOrderId: item.orderId })
                    } 
                });
            }
            return;
        }
        if (lowerTitle.includes('seller request') || lowerTitle.includes('seller application')) {
            if (['admin', 'superadmin'].includes(roleLower)) navigate('/admin/seller-requests', { state: { requestId: item.metaData?.requestId } });
            else navigate(`/${roleLower}/dashboard`);
            return;
        }
        if (lowerTitle.includes('credit') || lowerTitle.includes('wallet')) {
            if (roleLower === 'user') navigate('/buyer/rewards/credits');
            else navigate(`/${roleLower}/dashboard`);
            return;
        }
        if (item.type === 'coupon' || lowerTitle.includes('coupon')) {
            if (item.metaData?.couponCode) {
                navigator.clipboard.writeText(item.metaData.couponCode);
                message.success(`Coupon code ${item.metaData.couponCode} copied!`);
            }
            navigate('/buyer/offers');
            return;
        }


        navigate(roleLower === 'seller' ? '/seller/dashboard' : (['admin', 'superadmin'].includes(roleLower) ? '/admin/dashboard' : '/buyer/dashboard'));
    };

    const markAllRead = async () => {
        try {
            await NotificationServ.markAllAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            window.dispatchEvent(new Event("notification-refresh"));
            message.success("All marked as read");
        } catch (error) {
            message.error("Failed to mark all as read");
        }
    };

    const clearAll = async () => {
        try {
            await NotificationServ.clearAll();
            setNotifications([]);
            window.dispatchEvent(new Event("notification-refresh"));
            message.success("All notifications cleared");
        } catch (error) {
            message.error("Failed to clear notifications");
        }
    };



    const getIcon = (item) => {
        const type = item.type;
        const style = { 
            width: 36, 
            height: 36, 
            borderRadius: '10px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
        };

        switch (type) {
            case 'order': return <div style={{ ...style, backgroundColor: '#fff7ed' }}><IconTruckDelivery color="#FF8110" size={18} /></div>;
            case 'payment': return <div style={{ ...style, backgroundColor: '#f0fdf4' }}><IconReceipt2 color="#22c55e" size={18} /></div>;
            case 'system': return <div style={{ ...style, backgroundColor: '#eff6ff' }}><IconAlertCircle color="#3b82f6" size={18} /></div>;
            case 'coupon': return <div style={{ ...style, backgroundColor: '#f0f9ff' }}><IconTicket color="#0ea5e9" size={18} /></div>;
            default: return <div style={{ ...style, backgroundColor: '#f8fafc' }}><IconBell color="#64748b" size={18} /></div>;
        }
    };

    return (
        <div className="notifications-modern-page" style={{ padding: '24px 16px', maxWidth: 900, margin: '0 auto' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px', marginBottom: 24 }}>
                <div>
                    <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, color: '#1e293b' }}>
                        Notifications <Badge count={notifications.filter(n => !n.isRead).length} offset={[10, -5]} overflowCount={99} />
                    </h1>
                    <Text type="secondary" style={{ fontSize: 13 }}>Stay updated with your latest activities</Text>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    {notifications.length > 0 && (
                        <>
                            <Button size="small" onClick={markAllRead} icon={<IconCheck size={14} />}>Mark Read</Button>
                            <Button size="small" danger onClick={clearAll} icon={<IconAlertCircle size={14} />}>Clear All</Button>
                        </>
                    )}
                </div>
            </div>

            {loading && notifications.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 50 }}><Spin size="large" /></div>
            ) : notifications.length === 0 ? (
                <div style={{ background: 'white', borderRadius: 20, padding: 60, textAlign: 'center', border: '1px solid #f1f5f9' }}>
                    <EmptyState
                        title="No Notifications Yet"
                        description="You're all caught up! Check back later for updates."
                        gradient="purple"
                        showAction={false}
                        icon="inbox"
                    />
                </div>
            ) : (
                <div className="notifications-list-wrapper">
                    {notifications.map(item => (
                        <div 
                            key={item._id}
                            onClick={() => handleNotificationClick(item)}
                            className={`modern-notification-card ${!item.isRead ? 'unread' : ''}`}
                            style={{
                                background: !item.isRead 
                                    ? ((item.title?.toLowerCase().includes('reject') || item.title?.toLowerCase().includes('fail')) ? '#fef2f2' : '#fffcf8') 
                                    : 'white',
                                borderRadius: 16,
                                padding: '16px',
                                marginBottom: 12,
                                border: !item.isRead 
                                    ? ((item.title?.toLowerCase().includes('reject') || item.title?.toLowerCase().includes('fail')) ? '1px solid #fecaca' : '1px solid #fed7aa') 
                                    : '1px solid #f1f5f9',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                gap: 16,
                                position: 'relative',
                                boxShadow: item.isRead ? 'none' : '0 4px 12px rgba(0,0,0,0.03)'
                            }}
                        >
                            {!item.isRead && (
                                <div style={{ 
                                    position: 'absolute', left: 0, top: 16, bottom: 16, width: 4, 
                                    backgroundColor: (item.title?.toLowerCase().includes('reject') || item.title?.toLowerCase().includes('fail')) ? '#ef4444' : '#FF8110', borderRadius: '0 4px 4px 0' 
                                }} />
                            )}
                            
                            <div className="notif-icon">{getIcon(item)}</div>
                            
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4, gap: '8px' }}>
                                    <Text strong style={{ fontSize: 15, color: item.isRead ? '#475569' : '#1e293b' }}>
                                        {formatNotificationText(item.title)}
                                    </Text>
                                    <Text style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0, whiteSpace: 'nowrap' }}>
                                        {fromNow(item.createdAt)}
                                    </Text>
                                </div>
                                <div 
                                    style={{ fontSize: 13, color: item.isRead ? '#64748b' : '#334155', lineHeight: '1.5' }} 
                                    dangerouslySetInnerHTML={{ __html: formatNotificationText(item.description) }} 
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <style dangerouslySetInnerHTML={{ __html: `
                .modern-notification-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(0,0,0,0.06) !important;
                    border-color: #e2e8f0 !important;
                }
                .notifications-list-wrapper {
                    animation: fadeIn 0.4s ease-out;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}} />
        </div>
    );
};

export default NotificationsPage;
